import type { ItemFieldRow } from './item-field-reader';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { phpJsonInteger } from './php-integer-arithmetic';
import { readNodeItemId } from './layer-cell-readers';

type Row = Record<string, unknown>;
export const SNOW_GROUND_ID = '80BD5C08-9661-664B-D51F-0A6C4FBD0FDE';
export const ICE_GROUND_ID = '8110921E-DED4-9A70-C9F5-113B0ACFC443';
export const SLUGGISH_ID = '64318AE9-0187-4C71-9163-084694BCE180';
const VISION_SKILLS = new Set(['F99172CC-DDEF-48D4-87F8-8B94F0071247', '28E0D03E-86B3-FDF4-E03A-8DFA1043FE3E', 'DEC0A112-9ADC-09D3-0FA7-3A59CFE8C008']);
const list = (v: unknown): unknown[] => Array.isArray(v) ? v : v === undefined || v === null || v === '' ? [] : [v];
const id = (v: unknown): string => readLayerListEntryUuid(v).toUpperCase();

export interface EnvironmentImmunities { ignoresSnowAndIce: boolean; weatherVision: boolean }
export interface EnvironmentImmunityReaders {
    entries: (row: ItemFieldRow, key: string) => unknown[];
    value: (row: ItemFieldRow, key: string) => unknown;
    providers: (row: ItemFieldRow) => ItemFieldRow[];
    definition: (id: string) => ItemFieldRow | null;
    conditionsMet: (ability: ItemFieldRow) => boolean;
    isAura: (ability: ItemFieldRow) => boolean;
}
/** The weather merge runs before passive_conditions_active is stamped; evaluate the grantor itself. */
export function environmentImmunities(host: ItemFieldRow, weatherId: string, readers: EnvironmentImmunityReaders): EnvironmentImmunities {
    const result = { ignoresSnowAndIce: false, weatherVision: false };
    const collect = (row: ItemFieldRow): void => {
        result.ignoresSnowAndIce ||= readers.entries(row, 'ignores_snow_and_ice_terrain').some(phpJsonTruthy);
        result.weatherVision ||= weatherId !== '' && readers.entries(row, 'weather_vision_immunity').some(v => id(v) === weatherId.toUpperCase());
    };
    collect(host);
    const resolve = (entry: unknown): ItemFieldRow | null => {
        const nested = entry !== null && typeof entry === 'object' ? (entry as Row).item_arr : null;
        if (nested !== null && typeof nested === 'object') return nested as ItemFieldRow;
        const reference = id(entry);
        return reference ? readers.definition(reference) : null;
    };
    const hasRelevantImmunity = (ability: ItemFieldRow, ancestors = new Set<string>()): boolean => {
        const abilityId = readNodeItemId(ability) ?? '';
        if (abilityId !== '' && ancestors.has(abilityId)) return false;
        const next = new Set(ancestors);
        if (abilityId !== '') next.add(abilityId);
        if (readers.entries(ability, 'ignores_snow_and_ice_terrain').some(phpJsonTruthy)
            || readers.entries(ability, 'weather_vision_immunity').some(v => id(v) === weatherId.toUpperCase())) return true;
        return readers.entries(ability, 'passive_ability').some(entry => { const child = resolve(entry); return child !== null && hasRelevantImmunity(child, next); });
    };
    // meet_ability_requirements: PC-only, separate total/class gates, subclasses sharing their parent slot's level.
    const pc = (host as Row).player_characters;
    const isPc = pc !== null && typeof pc === 'object';
    const classLevels = new Map<string, bigint>(), carrierOf = new Map<string, string>();
    if (isPc) {
        const slots = pc as Row;
        for (let slot = 1; slot <= 20; slot++) {
            if (!phpJsonTruthy(slots[`class_${slot}`])) continue;
            const level = phpJsonInteger(slots[`class_level_${slot}`] ?? 0);
            for (const field of [`class_${slot}`, `subclass_${slot}`]) {
                const classId = phpJsonString(slots[field] ?? '');
                if (/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(classId)) classLevels.set(classId.toLowerCase(), level);
            }
        }
        for (const carrier of readers.providers(host)) {
            const carrierId = readNodeItemId(carrier) ?? '';
            for (const bucket of ['feat', 'passive_ability']) for (const entry of readers.entries(carrier, bucket)) {
                const abilityId = id(entry);
                if (!carrierOf.has(abilityId)) carrierOf.set(abilityId, carrierId);
            }
        }
    }
    const totalLevel = isPc ? phpJsonInteger(readers.value(host, 'total_level') ?? 0) : 0n;
    const seen = new Set<string>();
    const visit = (entry: unknown, parentSource = ''): void => {
        const abilityId = id(entry);
        const cell = entry !== null && typeof entry === 'object' ? entry as Row : null;
        const ownSource = phpJsonString(cell?.source ?? '');
        const source = ownSource !== '' ? ownSource : parentSource !== '' ? parentSource : carrierOf.get(abilityId) ?? '';
        const key = (abilityId + '|' + source).toUpperCase();
        if (seen.has(key)) return;
        seen.add(key);
        const ability = resolve(entry);
        if (!ability) return;
        const children = readers.entries(ability, 'passive_ability');
        if (!hasRelevantImmunity(ability) || readers.isAura(ability)) return;
        if (isPc) {
            const required = readers.value(ability, 'requires_level');
            if (required != null && required !== '' && phpJsonInteger(required) > totalLevel) return;
            const requiredClass = readers.value(ability, 'requires_class_level');
            if (requiredClass != null && requiredClass !== '' && phpJsonInteger(requiredClass) > (classLevels.get(source.toLowerCase()) ?? totalLevel)) return;
        }
        if (!readers.conditionsMet(ability)) return;
        collect(ability);
        for (const child of children) visit(child, source);
    };
    for (const bucket of ['passive_ability', 'feat']) for (const entry of readers.entries(host, bucket)) visit(entry);
    return result;
}

/** Filter only this donor's layers, preserving penalties from every other source. */
export function filterEnvironmentalLayers(layers: Row, immunities: EnvironmentImmunities, weather: boolean, wind: boolean, frozenGroundFromSource = false): Row {
    const out = { ...layers };
    const frozenGround = frozenGroundFromSource || list(layers.ground_terrain_type).some(v => [SNOW_GROUND_ID, ICE_GROUND_ID].includes(id(v)));
    if (weather && frozenGround && immunities.ignoresSnowAndIce) {
        for (const key of Object.keys(out)) if (key.startsWith('speed_')) delete out[key];
        if ('status_effect_permanent' in out) out.status_effect_permanent = list(out.status_effect_permanent).filter(v => id(v) !== SLUGGISH_ID);
    }
    // Snowblind Eyes covers wind obscuration alongside the immune weather (including blizzards); owner may tune this scope.
    if (immunities.weatherVision && (weather || wind)) {
        if ('skill_roll_dis' in out) out.skill_roll_dis = list(out.skill_roll_dis).filter(v => !VISION_SKILLS.has(id(v)));
        delete out.limited_visibility_ft;
    }
    return out;
}
